import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { ApiService } from 'src/services/api.service';
import { MessageService } from 'src/services/message.service';

interface KnowledgeGapInsightDetail {
  id: string;
  category: string;
  language: string;
  projectId?: string;
  createdAt: string;
  updatedAt: string;
  topics: {
    id: string;
    conversationId: string;
    topicTitle: string;
    topicDescription: string;
    context: string;
    confidence: number;
    createdAt: string;
  }[];
  recommendation?: {
    id: string;
    recommendationType: 'create' | 'update';
    existingArticleId?: string;
    existingArticleTitle?: string;
    ignoredAt?: string;
  };
}

@Component({
  selector: 'app-knowledge-gap-insight-detail',
  templateUrl: './knowledge-gap-insight-detail.component.html',
  styleUrls: ['./knowledge-gap-insight-detail.component.scss'],
})
export class KnowledgeGapInsightDetailComponent implements OnInit {
  insight: KnowledgeGapInsightDetail | null = null;
  tenantId: string | null = null;
  insightId: string | null = null;
  isLoading = false;
  error: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApiService,
    private messageService: MessageService,
  ) {}

  ngOnInit() {
    this.route.paramMap.subscribe((params) => {
      this.tenantId = params.get('tenantId');
      this.insightId = params.get('insightId');
      if (this.tenantId && this.insightId) {
        this.loadInsight();
      }
    });
  }

  async loadInsight() {
    if (!this.tenantId || !this.insightId) return;

    this.isLoading = true;
    this.error = null;

    try {
      const response = (await this.apiService.getPromise(
        `/tenants/${this.tenantId}/knowledge-gaps/insights/${this.insightId}`,
      )) as KnowledgeGapInsightDetail;

      this.insight = response;
    } catch (error) {
      this.error = 'Failed to load insight: ' + (error.message || 'Unknown error');
    } finally {
      this.isLoading = false;
    }
  }

  goBack() {
    this.router.navigate(['/super-admin/knowledge-gaps', this.tenantId]);
  }

  getRecommendationUrl(recommendationId: string): string {
    if (!this.tenantId) return '#';
    return `/super-admin/knowledge-gaps/${this.tenantId}/recommendations/${recommendationId}`;
  }

  getTopicUrl(topicId: string): string {
    if (!this.tenantId) return '#';
    return `/super-admin/knowledge-gaps/${this.tenantId}/topics/${topicId}`;
  }

  getConfidenceColor(score: number): string {
    if (score >= 0.8) return 'success';
    if (score >= 0.6) return 'warning';
    return 'danger';
  }

  getRecommendationTypeColor(type: string): string {
    return type === 'create' ? 'success' : 'warning';
  }

  getRecommendationTypeIcon(type: string): string {
    return type === 'create' ? 'add-circle' : 'create';
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString();
  }

  formatDateTime(dateString: string): string {
    return new Date(dateString).toLocaleString();
  }

  getUniqueConversationCount(): number {
    if (!this.insight?.topics) return 0;
    const uniqueConversationIds = new Set(this.insight.topics.map((topic) => topic.conversationId));
    return uniqueConversationIds.size;
  }

  getAverageConfidence(): number {
    if (!this.insight?.topics || this.insight.topics.length === 0) return 0;
    const sum = this.insight.topics.reduce((acc, topic) => acc + topic.confidence, 0);
    return sum / this.insight.topics.length;
  }
}
